import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  Avatar,
  Stack,
  Button,
} from "@mui/material";
import {
  People as PeopleIcon,
  ChevronRight as ChevronRightIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from "@mui/icons-material";
import { User } from "../../services/api";
import { lightTheme } from "../../styles/theme";
import { formatUserEmailDisplay } from "../../utils/userContactUtils";
import { getAvatarColor, getInitials } from "../../utils/avatarUtils";
import {
  MANAGEMENT_CARD_HEADER_FONT_SIZE,
  MANAGEMENT_CARD_META_FONT_SIZE,
  MANAGEMENT_CARD_TITLE_FONT_SIZE,
} from "../../styles/managementCardTypography";
import {
  MANAGEMENT_AVATAR_FONT_SIZE,
  MANAGEMENT_AVATAR_SIZE,
} from "../../styles/managementShared";

interface UserListProps {
  users: User[];
  onUserClick: (user: User) => void;
  isMobile: boolean;
  isLoading: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyActionLabel?: string;
  onEmptyAction?: () => void;
  emptyExtraContent?: React.ReactNode;
}

const DESKTOP_GRID_COLUMNS =
  "minmax(0, 2fr) minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1.5fr) auto";

type UserSortField = "name" | "joinedAt";
type UserSortDirection = "asc" | "desc";

const compareUsers = (
  a: User,
  b: User,
  field: UserSortField,
  direction: UserSortDirection
): number => {
  let result = 0;
  if (field === "name") {
    result = a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  } else {
    result =
      new Date(a.joinedAt).getTime() - new Date(b.joinedAt).getTime();
  }
  return direction === "asc" ? result : -result;
};

const SortArrows: React.FC<{
  field: UserSortField;
  activeField: UserSortField;
  direction: UserSortDirection;
}> = ({ field, activeField, direction }) => {
  const isActive = activeField === field;
  return (
    <Box
      sx={{
        display: "inline-flex",
        flexDirection: "column",
        lineHeight: 0,
        ml: 0.25,
      }}
      aria-hidden
    >
      <ArrowUpwardIcon
        sx={{
          fontSize: 14,
          color: isActive && direction === "asc" ? "primary.main" : "action.disabled",
          opacity: isActive && direction === "asc" ? 1 : 0.45,
        }}
      />
      <ArrowDownwardIcon
        sx={{
          fontSize: 14,
          mt: "-5px",
          color: isActive && direction === "desc" ? "primary.main" : "action.disabled",
          opacity: isActive && direction === "desc" ? 1 : 0.45,
        }}
      />
    </Box>
  );
};

const createStyles = () => ({
  headerCell: {
    fontWeight: 600,
    color: "text.secondary",
    textTransform: "uppercase" as const,
    fontSize: MANAGEMENT_CARD_HEADER_FONT_SIZE,
    letterSpacing: "0.5px",
  },
  mobileCard: {
    p: 1.5,
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 2,
    boxShadow: "none",
    display: "flex",
    alignItems: "center",
    flexDirection: "row" as const,
    gap: 1.5,
    minHeight: 56,
    cursor: "pointer",
  },
  mobileCardContent: {
    flex: 1,
    minWidth: 0,
  },
  mobileCardName: {
    fontSize: MANAGEMENT_CARD_TITLE_FONT_SIZE,
  },
  mobileCardEmail: {
    fontSize: MANAGEMENT_CARD_META_FONT_SIZE,
  },
  mobileCardDate: {
    fontSize: "0.6875rem",
  },
  mobileDateContainer: {
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "flex-end",
    gap: 0.25,
    flexShrink: 0,
  },
  sortableHeader: {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.25,
    border: "none",
    background: "none",
    padding: 0,
    cursor: "pointer",
    color: "inherit",
    font: "inherit",
    textAlign: "left" as const,
    "&:hover": {
      color: "primary.main",
    },
  },
});

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const UserList: React.FC<UserListProps> = React.memo(
  ({
    users,
    onUserClick,
    isMobile,
    isLoading,
    emptyTitle = "No users yet",
    emptyDescription = "Start by adding users to your organization.",
    emptyActionLabel,
    onEmptyAction,
    emptyExtraContent,
  }) => {
    const styles = useMemo(() => createStyles(), []);
    const [sortField, setSortField] = useState<UserSortField>("name");
    const [sortDirection, setSortDirection] = useState<UserSortDirection>("asc");

    const handleSort = useCallback(
      (field: UserSortField) => {
        if (sortField === field) {
          setSortDirection((currentDirection) =>
            currentDirection === "asc" ? "desc" : "asc"
          );
        } else {
          setSortField(field);
          setSortDirection("asc");
        }
      },
      [sortField]
    );

    const sortedUsers = useMemo(
      () =>
        [...users].sort((a, b) =>
          compareUsers(a, b, sortField, sortDirection)
        ),
      [users, sortField, sortDirection]
    );

    const renderSortableHeader = (
      field: UserSortField,
      label: string,
      mobileLabel?: string
    ) => (
      <Box
        component="button"
        type="button"
        onClick={() => handleSort(field)}
        aria-label={`Sort by ${label.toLowerCase()}, ${
          sortField === field
            ? sortDirection === "asc"
              ? "ascending"
              : "descending"
            : "not sorted"
        }`}
        sx={styles.sortableHeader}
      >
        <Typography component="span" sx={styles.headerCell}>
          {mobileLabel ?? label}
        </Typography>
        <SortArrows
          field={field}
          activeField={sortField}
          direction={sortDirection}
        />
      </Box>
    );

    const renderMobileSortControls = () => (
      <Stack direction="row" spacing={2} sx={{ mb: 1, px: 0.5 }}>
        {renderSortableHeader("name", "Name")}
        {renderSortableHeader("joinedAt", "Date Joined", "Date")}
      </Stack>
    );

    if (users.length === 0 && !isLoading) {
      return (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            color: "text.secondary",
          }}
        >
          <PeopleIcon
            sx={{
              fontSize: 64,
              color: "text.secondary",
              opacity: 0.5,
              mb: 2,
            }}
          />
          <Typography variant="body1" sx={{ mb: 1 }}>
            {emptyTitle}
          </Typography>
          <Typography variant="body2">{emptyDescription}</Typography>
          {emptyExtraContent}
          {emptyActionLabel && onEmptyAction ? (
            <Button
              variant="contained"
              onClick={onEmptyAction}
              sx={{ mt: 2.5 }}
            >
              {emptyActionLabel}
            </Button>
          ) : null}
        </Box>
      );
    }

    if (isMobile) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
          {renderMobileSortControls()}
          {sortedUsers.map((user) => (
            <Paper
              key={user.id}
              elevation={0}
              component="button"
              type="button"
              onClick={() => onUserClick(user)}
              sx={{
                ...styles.mobileCard,
                borderRadius: 1.5,
                textAlign: "left",
                width: "100%",
                bgcolor: "background.paper",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <Avatar
                sx={{
                  width: MANAGEMENT_AVATAR_SIZE,
                  height: MANAGEMENT_AVATAR_SIZE,
                  backgroundColor: getAvatarColor(user.name, lightTheme),
                  color: "common.white",
                  fontSize: MANAGEMENT_AVATAR_FONT_SIZE,
                  fontWeight: 600,
                  flexShrink: 0,
                }}
              >
                {getInitials(user.name)}
              </Avatar>
              <Box sx={styles.mobileCardContent}>
                <Typography
                  variant="subtitle2"
                  fontWeight={300}
                  noWrap
                  color="text.primary"
                  sx={styles.mobileCardName}
                >
                  {user.name}
                </Typography>
                {user.alias ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: "block", lineHeight: 1.3 }}
                  >
                    {user.alias}
                  </Typography>
                ) : null}
                {user.matchedFamilyLabel ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    noWrap
                    sx={{ display: "block", lineHeight: 1.3 }}
                  >
                    {user.matchedFamilyLabel}
                  </Typography>
                ) : null}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  noWrap
                  sx={styles.mobileCardEmail}
                >
                  {formatUserEmailDisplay(user.email)}
                </Typography>
                {user.phoneNumber && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    sx={{ fontSize: "0.75rem", mt: 0.5 }}
                  >
                    {user.phoneNumber}
                  </Typography>
                )}
              </Box>
              <Box sx={styles.mobileDateContainer}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={styles.mobileCardDate}
                >
                  {formatDate(user.joinedAt)}
                </Typography>
                <ChevronRightIcon
                  sx={{ fontSize: 20, color: "text.disabled" }}
                  aria-hidden
                />
              </Box>
            </Paper>
          ))}
        </Box>
      );
    }

    return (
      <Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: DESKTOP_GRID_COLUMNS,
            gap: 2,
            px: 3,
            py: 1,
            mb: 1,
          }}
        >
          {renderSortableHeader("name", "Name")}
          <Typography sx={styles.headerCell}>Email</Typography>
          <Typography sx={styles.headerCell}>Phone</Typography>
          {renderSortableHeader("joinedAt", "Date Joined")}
          <Box />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {sortedUsers.map((user) => (
            <Paper
              key={user.id}
              elevation={0}
              component="button"
              type="button"
              onClick={() => onUserClick(user)}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                cursor: "pointer",
                textAlign: "left",
                width: "100%",
                bgcolor: "background.paper",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: DESKTOP_GRID_COLUMNS,
                  gap: 2,
                  px: 3,
                  py: 2,
                  alignItems: "center",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, minWidth: 0 }}>
                  <Avatar
                    sx={{
                      width: MANAGEMENT_AVATAR_SIZE,
                      height: MANAGEMENT_AVATAR_SIZE,
                      backgroundColor: getAvatarColor(user.name, lightTheme),
                      color: "common.white",
                      fontSize: MANAGEMENT_AVATAR_FONT_SIZE,
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {getInitials(user.name)}
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        fontWeight: 500,
                        fontSize: MANAGEMENT_CARD_TITLE_FONT_SIZE,
                      }}
                    >
                      {user.name}
                    </Typography>
                    {user.alias ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{
                          display: "block",
                          fontSize: MANAGEMENT_CARD_META_FONT_SIZE,
                          lineHeight: 1.3,
                        }}
                      >
                        {user.alias}
                      </Typography>
                    ) : null}
                    {user.matchedFamilyLabel ? (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        sx={{
                          display: "block",
                          fontSize: MANAGEMENT_CARD_META_FONT_SIZE,
                          lineHeight: 1.3,
                        }}
                      >
                        {user.matchedFamilyLabel}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  noWrap
                  sx={{ fontSize: MANAGEMENT_CARD_META_FONT_SIZE }}
                >
                  {formatUserEmailDisplay(user.email)}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  noWrap
                  sx={{ fontSize: MANAGEMENT_CARD_META_FONT_SIZE }}
                >
                  {user.phoneNumber || "—"}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: MANAGEMENT_CARD_META_FONT_SIZE }}
                >
                  {formatDate(user.joinedAt)}
                </Typography>

                <ChevronRightIcon
                  sx={{ fontSize: 22, color: "text.disabled", justifySelf: "end" }}
                  aria-hidden
                />
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }
);

export default UserList;
